import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { authApi } from '../api/auth';

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  useEffect(() => {
    if (!token) {
      setError(t('resetPassword.noToken'));
    }
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError(t('resetPassword.passwordMismatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('resetPassword.passwordTooShort'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authApi.resetPassword({ token: token!, newPassword: password });
      setSuccess(true);
    } catch (err: any) {
      const message = err.response?.data?.message;
      if (message === 'invalid_or_expired_token') {
        setError(t('resetPassword.invalidToken'));
      } else {
        setError(message || t('resetPassword.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-6 animate-fade-in">
            <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
              <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
            </Link>
          </div>

          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('resetPassword.invalidLink')}
              </h1>
              <p className="text-gray-400 mb-6">
                {t('resetPassword.noToken')}
              </p>
              <Link
                to="/forgot-password"
                className="block w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all shadow-lg hover:shadow-glow text-center"
              >
                {t('resetPassword.requestNew')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <div className="relative">
          <button
            onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-primary-600/80 to-secondary-600/80 hover:from-primary-500 hover:to-secondary-500 rounded-lg transition-all flex items-center space-x-2 shadow-lg"
          >
            <span className="text-lg">🌐</span>
            <span className="uppercase font-bold">{i18n.language}</span>
            <span className={`text-xs transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}>▼</span>
          </button>
          {isLangMenuOpen && (
            <div className="absolute right-0 mt-2 w-40 bg-dark-900 border border-white/20 rounded-lg shadow-2xl z-50 animate-scale-in">
              {[
                { code: 'es', name: '🇪🇸 Español' },
                { code: 'en', name: '🇬🇧 English' },
                { code: 'ru', name: '🇷🇺 Русский' },
                { code: 'lt', name: '🇱🇹 Lietuvių' },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 first:rounded-t-lg last:rounded-b-lg transition-all ${
                    i18n.language === lang.code
                      ? 'bg-gradient-to-r from-primary-600/30 to-secondary-600/30 text-white font-semibold'
                      : 'text-gray-300'
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-md w-full">
        <div className="text-center mb-6 animate-fade-in">
          <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
            <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
          </Link>
          <p className="text-gray-400 text-base sm:text-lg px-4">{t('resetPassword.subtitle')}</p>
        </div>

        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
          {success ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {t('resetPassword.successTitle')}
              </h2>
              <p className="text-gray-400 mb-6">
                {t('resetPassword.successDescription')}
              </p>
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all shadow-lg hover:shadow-glow"
              >
                {t('resetPassword.goToLogin')}
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary-400" />
                </div>
                <h2 className="text-xl font-bold text-white">
                  {t('resetPassword.title')}
                </h2>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 text-red-200 rounded-xl backdrop-blur-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('resetPassword.newPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-500"
                      placeholder={t('resetPassword.newPasswordPlaceholder')}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-300 mb-2">
                    {t('resetPassword.confirmPassword')}
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 text-white rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder-gray-500"
                      placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-glow"
                >
                  {loading ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
